import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import DialCard from './DialCard';
import apiClient from '../../utils/api';

// Section 2 of the home page: the composable "Dials" — the adjustable metrics
// you're designing. Each pinned metric is a full-width start → now → next →
// target journey that expands into its full trajectory chart (the retired /twin
// page, folded into home). Add/remove dials from the metric registry.

const DialsSection = ({ refreshKey }) => {
  const [cards, setCards] = useState(null);
  const [byKey, setByKey] = useState(new Map());
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Fire-and-forget: regenerate any stale/missing AI tips in a separate request
  // so the dials render instantly and the coaching lines stream in a beat later.
  // Only runs when the user is actually here looking at them — never in the
  // background, never for logged-out users.
  const refreshSuggestions = useCallback((cardList) => {
    if (!cardList?.some((c) => c.suggestion_stale)) return;
    apiClient
      .post('/api/twin/cards/suggestions')
      .then((res) => {
        setCards((prev) =>
          prev
            ? prev.map((c) =>
                c.metric_key in res.data
                  ? { ...c, suggestion: res.data[c.metric_key], suggestion_stale: false }
                  : c
              )
            : prev
        );
      })
      .catch(() => {
        // Leave the placeholder; a later visit retries. Never blocks the UI.
      });
  }, []);

  const load = useCallback(async () => {
    try {
      const [cardRes, trajRes, targetRes] = await Promise.all([
        apiClient.get('/api/twin/cards'),
        apiClient.get('/api/twin/trajectory'),
        apiClient.get('/api/twin/targets'),
      ]);
      const targetMap = new Map(targetRes.data.map((t) => [t.metric_key, t]));
      const merged = new Map(
        trajRes.data.map((tr) => [
          tr.metric_key,
          { ...tr, target: targetMap.get(tr.metric_key) || {} },
        ])
      );
      setCards(cardRes.data);
      setByKey(merged);
      setTargets(targetRes.data);
      refreshSuggestions(cardRes.data);
    } catch (error) {
      toast.error('Failed to load your dials');
    } finally {
      setLoading(false);
    }
  }, [refreshSuggestions]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // A target changed on one of the cards. Reflect it instantly in the editor,
  // then reload cards + trajectory so the ring, journey track and chart band all
  // move to the new target.
  const handleTargetSaved = useCallback((updated) => {
    setTargets((prev) =>
      prev.map((t) => (t.metric_key === updated.metric_key ? updated : t))
    );
    load();
  }, [load]);

  const setPin = async (metricKey, pinned) => {
    setBusy(true);
    try {
      await apiClient.patch(`/api/twin/cards/${metricKey}`, { pinned });
      const cardRes = await apiClient.get('/api/twin/cards');
      setCards(cardRes.data);
      // keep the targets list's pinned flags in sync for the picker
      setTargets((prev) =>
        prev.map((t) => (t.metric_key === metricKey ? { ...t, pinned } : t))
      );
      // a freshly pinned dial has no tip yet — generate it
      if (pinned) refreshSuggestions(cardRes.data);
    } catch (error) {
      toast.error('Could not update your dials');
    } finally {
      setBusy(false);
    }
  };

  // Metrics available to add: everything with a target that isn't already
  // pinned. The mitochondrial index is in here too — it keeps its hero card on
  // the Data page, but it's a metric you're designing like any other, so it can
  // ride as a dial when you want it in front of you.
  const available = useMemo(
    () => targets.filter((t) => !t.pinned && t.enabled),
    [targets]
  );

  // The full target object per metric, so each dial can set its own knob inline.
  const targetByKey = useMemo(
    () => new Map(targets.map((t) => [t.metric_key, t])),
    [targets]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!cards) return null;

  return (
    <section>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Your dials</h2>
          <p className="text-base text-gray-600 dark:text-gray-300">
            The metrics you're designing — started, now, and where you're headed.
          </p>
        </div>
        <div className="relative w-full sm:w-auto sm:shrink-0">
          <button
            onClick={() => setPickerOpen((v) => !v)}
            disabled={available.length === 0}
            className="w-full sm:w-auto text-base font-medium px-3.5 py-2 rounded-lg border border-surface-border dark:border-surface-dark-border bg-gray-100 dark:bg-surface-dark-raised text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-40 transition-colors"
          >
            + Add a dial
          </button>
          {pickerOpen && available.length > 0 && (
            <div className="absolute right-0 mt-1 w-56 max-h-72 overflow-y-auto rounded-lg border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card shadow-lg z-20 py-1">
              {available.map((t) => (
                <button
                  key={t.metric_key}
                  disabled={busy}
                  onClick={() => {
                    setPin(t.metric_key, true);
                    setPickerOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 transition-colors"
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {cards.length === 0 ? (
        <div className="rounded-2xl border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card p-8 text-center">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            No dials pinned yet. Add one to start designing a metric.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {cards.map((card) => (
            <DialCard
              key={card.metric_key}
              card={card}
              metric={byKey.get(card.metric_key)}
              target={targetByKey.get(card.metric_key)}
              onTargetSaved={handleTargetSaved}
              onUnpin={(k) => setPin(k, false)}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default DialsSection;
